import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button } from "@admin/components/ui/button";
import { Input } from "@admin/components/ui/input";
import { Textarea } from "@admin/components/ui/textarea";
import { Label } from "@admin/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@admin/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@admin/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@admin/components/ui/popover";
import { Checkbox } from "@admin/components/ui/checkbox";
import { useToast } from "@admin/hooks/use-toast";
import {
  CheckCircle2,
  GraduationCap,
  ChevronDown,
  KeyRound,
  ArrowLeft,
} from "lucide-react";
import { cn } from "@admin/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@admin/components/ui/dialog";
import {
  studentAdmissionInquiryAPI,
  authAPI,
  userRolesAPI,
  countriesAPI,
  zonesAPI,
  statesAPI,
  citiesAPI,
  districtsAPI,
  type Zone,
  type State,
  type City,
  type Area,
  type District,
} from "../../services/api";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@admin/components/ui/input-otp";
import { useAuth } from "../../contexts/AuthContext";

type StudentInquiryFormValues = {
  fullName: string;
  address: string;
  email: string;
  mobile: string;
  pincode: string;
  courseStdName: string;
  collegeSchool: string;
  lastYearRemark: string;
  lastResultPercentage: string;
  zone: string;
  state: string;
  district: string;
  preferredCities: string[];
  preferredAreas: string[];
  hostelRemark: string;
};

// Step 1: only required fields validated so Next works when study details are left empty
const STEP_FIELDS: Record<number, (keyof StudentInquiryFormValues)[]> = {
  1: ["fullName", "address", "email", "mobile", "pincode"],
  2: [
    "zone",
    "state",
    "district",
    "preferredCities",
    "preferredAreas",
    "hostelRemark",
  ],
};

const STEP_TITLES: Record<number, string> = {
  1: "Student & Study Details",
  2: "Institution Location Preference",
  3: "Verify OTP",
};

export default function StudentRegistrationForm() {
  const [step, setStep] = useState(1);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [zones, setZones] = useState<Zone[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [codeReference, setCodeReference] = useState("");
  const [otp, setOtp] = useState("");
  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const { toast } = useToast();
  const { updateAuth } = useAuth();
  const rootNavigate = useNavigate();

  const form = useForm<StudentInquiryFormValues>({
    mode: "onChange",
    defaultValues: {
      fullName: "",
      address: "",
      email: "",
      mobile: "",
      pincode: "",
      courseStdName: "",
      collegeSchool: "",
      lastYearRemark: "",
      lastResultPercentage: "",
      zone: "",
      state: "",
      district: "",
      preferredCities: [],
      preferredAreas: [],
      hostelRemark: "",
    },
  });

  const selectedZoneId = form.watch("zone");
  const selectedStateId = form.watch("state");
  const selectedDistrictId = form.watch("district");
  const selectedCityIds = form.watch("preferredCities") ?? [];

  const districtsRef = useRef<District[]>([]);
  districtsRef.current = districts;

  const [zonesLoading, setZonesLoading] = useState(false);
  const [statesLoading, setStatesLoading] = useState(false);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [areasLoading, setAreasLoading] = useState(false);
  const [defaultCountryId, setDefaultCountryId] = useState<
    number | string | null
  >(null);
  const [dynamicPhoneCode, setDynamicPhoneCode] = useState<string>("91");

  // Fetch countries and find India to set dynamic country ID and phone code
  useEffect(() => {
    countriesAPI
      .list()
      .then(list => {
        if (Array.isArray(list) && list.length > 0) {
          const india =
            list.find(c => c.name.toLowerCase() === "india") ?? list[0];
          if (india) {
            setDefaultCountryId(india.id);
            setDynamicPhoneCode(india.code ?? "91");
          }
        }
      })
      .catch(err => {
        console.error("Failed to fetch countries:", err);
      });
  }, []);

  // Zones: fetch when step 2 is shown (using dynamic country id)
  useEffect(() => {
    if (step !== 2 || !defaultCountryId) {
      return;
    }
    let cancelled = false;
    setZonesLoading(true);
    countriesAPI
      .getZonesByCountryId(defaultCountryId)
      .then(zList => {
        if (!cancelled) {
          setZones(Array.isArray(zList) ? zList : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast({
            title: "Error",
            description: "Failed to load zones",
            variant: "destructive",
          });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setZonesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [step, toast, defaultCountryId]);

  // States: fetch by zone_id when zone is selected
  useEffect(() => {
    if (!selectedZoneId) {
      setStates([]);
      return;
    }
    let cancelled = false;
    setStatesLoading(true);
    zonesAPI
      .getStatesByZoneId(selectedZoneId)
      .then(sList => {
        if (!cancelled) {
          setStates(Array.isArray(sList) ? sList : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast({
            title: "Error",
            description: "Failed to load states",
            variant: "destructive",
          });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setStatesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedZoneId, toast]);

  // Districts: fetch by state when state is selected
  useEffect(() => {
    if (!selectedStateId) {
      setDistricts([]);
      setCities([]);
      return;
    }
    let cancelled = false;
    setDistrictsLoading(true);
    setCities([]);
    statesAPI
      .getDistrictsByStateId(selectedStateId)
      .then(dList => {
        if (cancelled) {
          return;
        }
        const list = Array.isArray(dList) ? dList : [];
        setDistricts(list);
        if (list.length === 0) {
          setCitiesLoading(true);
          statesAPI
            .getCitiesByStateId(selectedStateId)
            .then(cList => {
              if (!cancelled) {
                setCities(Array.isArray(cList) ? cList : []);
              }
            })
            .catch(() => {
              if (!cancelled) {
                toast({
                  title: "Error",
                  description: "Failed to load cities",
                  variant: "destructive",
                });
                setCities([]);
              }
            })
            .finally(() => {
              if (!cancelled) {
                setCitiesLoading(false);
              }
            });
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast({
            title: "Error",
            description: "Failed to load districts",
            variant: "destructive",
          });
          setDistricts([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDistrictsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedStateId, toast]);

  // Cities: by district when the state has districts; otherwise loaded in districts effect
  useEffect(() => {
    if (!selectedStateId || districts.length === 0) {
      return;
    }
    if (!selectedDistrictId) {
      setCities([]);
      return;
    }
    let cancelled = false;
    setCitiesLoading(true);
    districtsAPI
      .getCitiesByDistrictId(selectedDistrictId)
      .then(cList => {
        if (!cancelled) {
          setCities(Array.isArray(cList) ? cList : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast({
            title: "Error",
            description: "Failed to load cities",
            variant: "destructive",
          });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCitiesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedStateId, selectedDistrictId, districts.length, toast]);

  // Areas: fetch by city_id for each selected city, then merge
  const selectedCityIdsKey = selectedCityIds?.join(",") ?? "";
  useEffect(() => {
    if (!selectedCityIds?.length) {
      setAreas([]);
      return;
    }
    let cancelled = false;
    setAreasLoading(true);
    const cityIds = [...selectedCityIds];
    Promise.all(cityIds.map(cityId => citiesAPI.getAreasByCityId(cityId)))
      .then(results => {
        if (cancelled) {
          return;
        }
        const merged: Area[] = [];
        const seen = new Set<string | number>();
        results.flat().forEach(a => {
          if (!seen.has(a.id)) {
            seen.add(a.id);
            merged.push(a);
          }
        });
        setAreas(merged);
      })
      .catch(() => {
        if (!cancelled) {
          toast({
            title: "Error",
            description: "Failed to load areas",
            variant: "destructive",
          });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAreasLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCityIdsKey, toast]);

  const statesByZone = states;
  const citiesByState = cities;
  const areasBySelectedCities = areas;

  const handleNext = async () => {
    const isValid = await form.trigger(STEP_FIELDS[step]);
    if (!isValid) {
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const onZoneChange = (value: string) => {
    form.setValue("zone", value, { shouldValidate: true });
    form.setValue("state", "", { shouldValidate: false });
    form.setValue("district", "", { shouldValidate: false });
    form.setValue("preferredCities", [], { shouldValidate: false });
    form.setValue("preferredAreas", [], { shouldValidate: false });
  };

  const onStateChange = (value: string) => {
    form.setValue("state", value, { shouldValidate: true });
    form.setValue("district", "", { shouldValidate: false });
    form.setValue("preferredCities", [], { shouldValidate: false });
    form.setValue("preferredAreas", [], { shouldValidate: false });
  };

  const onDistrictChange = (value: string) => {
    form.setValue("district", value, { shouldValidate: true });
    form.setValue("preferredCities", [], { shouldValidate: false });
    form.setValue("preferredAreas", [], { shouldValidate: false });
  };

  const toggleCity = (cityId: string) => {
    const selected = form.getValues("preferredCities") || [];
    const next = selected.includes(cityId)
      ? selected.filter(id => id !== cityId)
      : [...selected, cityId];
    form.setValue("preferredCities", next, { shouldValidate: true });
    form.setValue("preferredAreas", [], { shouldValidate: false });
  };

  const toggleArea = (areaId: string) => {
    const selected = form.getValues("preferredAreas") || [];
    const next = selected.includes(areaId)
      ? selected.filter(id => id !== areaId)
      : [...selected, areaId];
    form.setValue("preferredAreas", next, { shouldValidate: true });
  };

  const handleSendOtp = async () => {
    const isValid = await form.trigger(STEP_FIELDS[2]);
    if (!isValid) {
      return;
    }
    const mobile =
      form.getValues("mobile")?.replace(/\D/g, "").slice(0, 10) ?? "";
    if (mobile.length !== 10) {
      toast({
        title: "Invalid mobile",
        description: "Please enter a valid 10-digit mobile number in Step 1.",
        variant: "destructive",
      });
      return;
    }
    setSendOtpLoading(true);
    try {
      const phoneCode = import.meta.env.VITE_PHONE_CODE as string;
      const response = await authAPI.sendOTP(mobile, phoneCode, true);
      if (response?.code_reference) {
        setCodeReference(response.code_reference);
        toast({
          title: "OTP Sent",
          description: `OTP sent to +${dynamicPhoneCode} ${mobile}. Enter it below.`,
        });
        setStep(3);
        setOtp("");
      } else {
        throw new Error("No code_reference received");
      }
    } catch (error: any) {
      toast({
        title: "Failed to send OTP",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSendOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    const mobile =
      form.getValues("mobile")?.replace(/\D/g, "").slice(0, 10) ?? "";
    if (mobile.length !== 10 || !codeReference) {
      toast({
        title: "Cannot resend",
        description: "Mobile number or code reference missing.",
        variant: "destructive",
      });
      return;
    }
    setSendOtpLoading(true);
    try {
      await authAPI.resendOTP(mobile, codeReference);
      toast({
        title: "OTP resent",
        description: "Check your mobile for the new OTP.",
      });
    } catch (error: any) {
      const phoneCode = import.meta.env.VITE_PHONE_CODE as string;
      const response = await authAPI.sendOTP(mobile, phoneCode, true);
      if (response?.code_reference) {
        setCodeReference(response.code_reference);
        toast({ title: "OTP sent again", description: "Check your mobile." });
      } else {
        toast({
          title: "Resend failed",
          description:
            error?.response?.data?.message ?? error?.message ?? "Try again.",
          variant: "destructive",
        });
      }
    } finally {
      setSendOtpLoading(false);
    }
  };

  const handleVerifyAndSubmit = async () => {
    if (otp.trim().length < 6) {
      toast({
        title: "Invalid OTP",
        description: "Please enter the 6-digit OTP.",
        variant: "destructive",
      });
      return;
    }
    if (!codeReference) {
      toast({
        title: "Session expired",
        description: "Please go back and click Verify OTP again.",
        variant: "destructive",
      });
      return;
    }
    setVerifyLoading(true);
    try {
      // 1. Call student-admission-inquiry API first
      const data = form.getValues();
      const isSubmissionSuccess = await handleFinalSubmit(data);

      if (!isSubmissionSuccess) {
        return;
      }

      // 2. If submission success, call the auth API verifyOTP
      const authResponse = await authAPI.verifyOTP(
        otp.trim(),
        codeReference,
        true,
        data.fullName
      );

      const isOtpSuccess =
        authResponse &&
        typeof authResponse === "object" &&
        (authResponse as { access?: string }).access;

      if (!isOtpSuccess) {
        toast({
          title: "OTP verification failed",
          description:
            (authResponse as { message?: string })?.message ??
            "Invalid or expired OTP. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // After OTP verification success: match Auth.tsx registration flow
      // 1. Create a profile entry
      // try {
      //   await profilesAPI.upsert({
      //     user_id: authResponse.user.id,
      //     phone: `+${dynamicPhoneCode}${data.mobile.replace(/\D/g, "").slice(0, 10)}`,
      //   });
      // } catch (profileErr) {
      //   console.error("Profile creation failed:", profileErr);
      // }

      // 2. Create the user role as "student"
      try {
        await userRolesAPI.create({
          user_id: authResponse.user.id,
          jbf_role: "student",
        });
      } catch (roleErr) {
        console.error("User role creation failed:", roleErr);
      }

      // 3. Update auth context so the user is logged in and profile is fetched
      // fetchUserRole will now find the student role and call getMyProfile
      await updateAuth(
        authResponse.user,
        authResponse.access,
        authResponse.refresh,
        authResponse.jbf_role ?? "student"
      );

      // 3. Finally set ShowSuccessModal open
      setShowSuccessModal(true);
    } catch (error: any) {
      toast({
        title: "Submission failed",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleFinalSubmit = async (data: StudentInquiryFormValues) => {
    try {
      const result = (await studentAdmissionInquiryAPI.create({
        full_name: data.fullName,
        address: data.address,
        email: data.email,
        mobile: data.mobile,
        pincode: data.pincode,
        course_std_name: data.courseStdName || null,
        college_school: data.collegeSchool || null,
        last_year_remark: data.lastYearRemark || null,
        last_result_percentage: String(data.lastResultPercentage || ""),
        preferred_zone: Number(data.zone),
        preferred_state: Number(data.state),
        ...(data.district && String(data.district).length > 0
          ? { preferred_district: Number(data.district) }
          : {}),
        preferred_cities: (data.preferredCities || []).map(Number),
        preferred_areas: (data.preferredAreas || []).map(Number),
        hostel_remark: data.hostelRemark || null,
      })) as
        | {
            message?: string;
            status_code?: number;
            data?: unknown;
            id?: unknown;
          }
        | undefined;

      // axiosUitils returns undefined on failed API calls; treat as failure and do not open modal.
      if (!result) {
        toast({
          title: "Submission Failed",
          description: "Failed to submit inquiry. Please try again.",
          variant: "destructive",
        });
        return false;
      }

      const statusCode = result.status_code;
      const isSuccessStatus = statusCode === 201 || Number(statusCode) === 201;
      const hasData = result.data !== undefined || result.id !== undefined;
      const isSuccessMessage =
        typeof result.message === "string" &&
        /created successfully|success|submitted/i.test(result.message);

      const isSuccess = isSuccessStatus || hasData || isSuccessMessage;

      if (!isSuccess) {
        toast({
          title: "Submission Failed",
          description: result.message ?? "Failed to submit inquiry",
          variant: "destructive",
        });
        return false;
      }

      toast({
        title: "Inquiry Submitted!",
        description:
          result.message ??
          "Student admission inquiry has been submitted successfully.",
      });
      return true;
    } catch (error: any) {
      toast({
        title: "Submission Failed",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "Failed to submit inquiry",
        variant: "destructive",
      });
      return false;
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <Card>
        <CardHeader>
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-600">
              <GraduationCap className="h-6 w-6 text-white" />
            </div>
            <div>
              <CardTitle>Student Admission Inquiry</CardTitle>
              <CardDescription>
                Step {step} of 3 - {STEP_TITLES[step]}
              </CardDescription>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            {[1, 2, 3].map(s => (
              <div
                key={s}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  s <= step ? "bg-primary" : "bg-muted"
                }`}
              />
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {step === 1 && (
            <form className="space-y-4">
              <h3 className="mb-4 text-lg font-semibold">
                Student & Study Details
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    {...form.register("fullName", {
                      required: "Full name is required",
                    })}
                  />
                  {form.formState.errors.fullName && (
                    <p className="mt-1 text-sm text-red-600">
                      {form.formState.errors.fullName.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label>
                    Email ID <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="email"
                    {...form.register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Please enter a valid email id",
                      },
                    })}
                  />
                  {form.formState.errors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {form.formState.errors.email.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label>
                    Mobile No <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    {...form.register("mobile", {
                      required: "Mobile number is required",
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: "Please enter a valid 10 digit mobile number",
                      },
                    })}
                    maxLength={10}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, "");
                      form.setValue("mobile", val, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                  />
                  {form.formState.errors.mobile && (
                    <p className="mt-1 text-sm text-red-600">
                      {form.formState.errors.mobile.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label>
                    Pincode <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    {...form.register("pincode", {
                      required: "Pincode is required",
                      pattern: {
                        value: /^[0-9]{6}$/,
                        message: "Please enter a valid 6 digit pincode",
                      },
                    })}
                    maxLength={6}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, "");
                      form.setValue("pincode", val, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                  />
                  {form.formState.errors.pincode && (
                    <p className="mt-1 text-sm text-red-600">
                      {form.formState.errors.pincode.message}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <Label>
                  Address <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  {...form.register("address", {
                    required: "Address is required",
                  })}
                />
                {form.formState.errors.address && (
                  <p className="mt-1 text-sm text-red-600">
                    {form.formState.errors.address.message}
                  </p>
                )}
              </div>
              <div className="border-t pt-4">
                <h4 className="mb-3 text-sm font-medium text-muted-foreground">
                  Last Year Study Details (Optional)
                </h4>
                <div className="space-y-4">
                  <div>
                    <Label>Course/Std Name</Label>
                    <Input
                      {...form.register("courseStdName")}
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <Label>College/School</Label>
                    <Input
                      {...form.register("collegeSchool")}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Remark</Label>
                      <Textarea
                        {...form.register("lastYearRemark")}
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <Label>Last Result Percentage</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="Optional (0–100)"
                        onWheel={e => e.currentTarget.blur()}
                        onKeyDown={e => {
                          if (["e", "E", "-", "+"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        {...form.register("lastResultPercentage", {
                          min: {
                            value: 0,
                            message: "Percentage cannot be negative",
                          },
                          max: {
                            value: 100,
                            message: "Percentage cannot exceed 100",
                          },
                          validate: value => {
                            if (value === "" || value === null) {
                              return true;
                            }
                            const parsed = Number(value);
                            if (Number.isNaN(parsed)) {
                              return "Must be a number";
                            }
                            if (parsed < 0 || parsed > 100) {
                              return "Percentage must be between 0 and 100";
                            }
                            return true;
                          },
                        })}
                      />
                      {form.formState.errors.lastResultPercentage && (
                        <p className="mt-1 text-sm text-red-600">
                          {form.formState.errors.lastResultPercentage.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <Button
                type="button"
                onClick={handleNext}
                className="w-full"
                size="lg"
              >
                Next
              </Button>
            </form>
          )}

          {step === 2 && (
            <form
              className="space-y-4"
              onSubmit={e => {
                e.preventDefault();
                handleSendOtp();
              }}
            >
              <h3 className="mb-4 text-lg font-semibold">
                Institution Location Preference
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                <div>
                  <Label>Zone</Label>
                  <Select
                    value={form.watch("zone")}
                    onValueChange={onZoneChange}
                    disabled={zonesLoading}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          zonesLoading ? "Loading zones..." : "Select zone"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {zones.map(z => (
                        <SelectItem key={z.id} value={String(z.id)}>
                          {z.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input type="hidden" {...form.register("zone")} />
                  {form.formState.errors.zone && (
                    <p className="mt-1 text-sm text-red-600">
                      {form.formState.errors.zone.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label>State</Label>
                  <Select
                    value={form.watch("state")}
                    onValueChange={onStateChange}
                    disabled={!selectedZoneId || statesLoading}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          statesLoading ? "Loading states..." : "Select state"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {statesByZone.map(s => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input type="hidden" {...form.register("state")} />
                  {form.formState.errors.state && (
                    <p className="mt-1 text-sm text-red-600">
                      {form.formState.errors.state.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label>District</Label>
                  <Select
                    value={form.watch("district")}
                    onValueChange={onDistrictChange}
                    disabled={
                      !selectedStateId ||
                      districtsLoading ||
                      districts.length === 0
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          !selectedStateId
                            ? "Select state first"
                            : districtsLoading
                              ? "Loading districts..."
                              : districts.length === 0
                                ? "No districts (cities by state)"
                                : "Select district"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {districts.map(d => (
                        <SelectItem key={d.id} value={String(d.id)}>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input
                    type="hidden"
                    {...form.register("district", {
                      validate: v => {
                        if (districtsRef.current.length === 0) {
                          return true;
                        }
                        return (
                          (v !== null && String(v).length > 0) ||
                          "Please select a district"
                        );
                      },
                    })}
                  />
                  {form.formState.errors.district && (
                    <p className="mt-1 text-sm text-red-600">
                      {form.formState.errors.district.message}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <Label>City (Multiple Selectionnn)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      className={cn(
                        "mt-2 w-full justify-between font-normal",
                        !form.watch("preferredCities")?.length &&
                          "text-muted-foreground"
                      )}
                      disabled={
                        !selectedStateId ||
                        citiesLoading ||
                        (districts.length > 0 && !selectedDistrictId)
                      }
                    >
                      <span className="truncate">
                        {(form.watch("preferredCities")?.length ?? 0) === 0
                          ? citiesLoading
                            ? "Loading cities..."
                            : "Select cities..."
                          : `${form.watch("preferredCities")?.length} city/cities selected`}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <div className="max-h-60 overflow-y-auto p-2">
                      {!selectedStateId ? (
                        <p className="py-4 text-center text-sm text-muted-foreground">
                          Select zone and state first
                        </p>
                      ) : districts.length > 0 && !selectedDistrictId ? (
                        <p className="py-4 text-center text-sm text-muted-foreground">
                          Select a district to load cities
                        </p>
                      ) : citiesLoading ? (
                        <p className="py-4 text-center text-sm text-muted-foreground">
                          Loading cities...
                        </p>
                      ) : citiesByState.length === 0 ? (
                        <p className="py-4 text-center text-sm text-muted-foreground">
                          {districts.length > 0
                            ? "No cities in this district"
                            : "No cities in this state"}
                        </p>
                      ) : (
                        citiesByState.map(c => (
                          <label
                            key={c.id}
                            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 hover:bg-muted/50"
                          >
                            <Checkbox
                              checked={(
                                form.watch("preferredCities") || []
                              ).includes(String(c.id))}
                              onCheckedChange={() => toggleCity(String(c.id))}
                            />
                            <span className="text-sm">{c.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <input type="hidden" {...form.register("preferredCities")} />
                {form.formState.errors.preferredCities && (
                  <p className="mt-1 text-sm text-red-600">
                    {form.formState.errors.preferredCities.message}
                  </p>
                )}
              </div>
              <div>
                <Label>Area (Multiple Selection)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      className={cn(
                        "mt-2 w-full justify-between font-normal",
                        !form.watch("preferredAreas")?.length &&
                          "text-muted-foreground"
                      )}
                      disabled={!selectedCityIds?.length || areasLoading}
                    >
                      <span className="truncate">
                        {(form.watch("preferredAreas")?.length ?? 0) === 0
                          ? areasLoading
                            ? "Loading areas..."
                            : "Select areas..."
                          : `${form.watch("preferredAreas")?.length} area/areas selected`}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <div className="max-h-60 overflow-y-auto p-2">
                      {!selectedCityIds?.length ? (
                        <p className="py-4 text-center text-sm text-muted-foreground">
                          Select at least one city first
                        </p>
                      ) : areasLoading ? (
                        <p className="py-4 text-center text-sm text-muted-foreground">
                          Loading areas...
                        </p>
                      ) : areasBySelectedCities.length === 0 ? (
                        <p className="py-4 text-center text-sm text-muted-foreground">
                          No areas for selected cities
                        </p>
                      ) : (
                        areasBySelectedCities.map(a => (
                          <label
                            key={a.id}
                            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 hover:bg-muted/50"
                          >
                            <Checkbox
                              checked={(
                                form.watch("preferredAreas") || []
                              ).includes(String(a.id))}
                              onCheckedChange={() => toggleArea(String(a.id))}
                            />
                            <span className="text-sm">{a.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <input type="hidden" {...form.register("preferredAreas")} />
                {form.formState.errors.preferredAreas && (
                  <p className="mt-1 text-sm text-red-600">
                    {form.formState.errors.preferredAreas.message}
                  </p>
                )}
              </div>
              <div>
                <Label>Remark (Optional)</Label>
                <Textarea
                  {...form.register("hostelRemark")}
                  placeholder="Any additional preference or note"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="flex-1"
                  size="lg"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                  size="lg"
                  disabled={sendOtpLoading}
                >
                  {sendOtpLoading ? "Sending OTP..." : "Verify OTP"}
                </Button>
              </div>
            </form>
          )}

          {step === 3 && (
            <div className="mx-auto flex max-w-sm flex-col items-center py-6 text-center">
              <h3 className="text-2xl font-bold text-foreground">Verify OTP</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Enter the 6-digit code sent to +91{" "}
                {form.watch("mobile")?.replace(/\D/g, "").slice(0, 10) || "—"}
              </p>
              <div className="mt-6 flex h-16 w-16 items-center justify-center rounded-full border-2 border-rose-300 bg-rose-50">
                <KeyRound className="h-8 w-8 text-rose-700" />
              </div>
              <div className="mt-8 flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={setOtp}
                  data-testid="otp-input"
                  containerClassName="gap-3"
                >
                  <InputOTPGroup className="gap-3">
                    {[0, 1, 2, 3, 4, 5].map(index => (
                      <InputOTPSlot
                        key={index}
                        index={index}
                        className="h-12 w-12 rounded-lg border border-slate-200 bg-white text-center text-lg font-medium text-foreground shadow-sm first:rounded-lg first:border last:rounded-lg last:border"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                type="button"
                onClick={handleVerifyAndSubmit}
                className="mt-8 w-full rounded-full bg-primary px-6 py-6 text-base font-semibold text-white hover:bg-primary/90"
                size="lg"
                disabled={verifyLoading || otp.trim().length < 6}
              >
                {verifyLoading ? "Verifying..." : "Verify & Submit"}
              </Button>
              <div className="mt-8 flex w-full items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Change Number
                </button>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={sendOtpLoading}
                  className="font-medium text-rose-700 hover:text-rose-800 disabled:opacity-50"
                >
                  {sendOtpLoading ? "Sending..." : "Resend OTP"}
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md" showCloseButton={false}>
          <div className="mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircle2 className="h-9 w-9 text-green-600" />
          </div>
          <DialogHeader className="text-center">
            <DialogTitle className="text-center">
              Form Submitted Successfully
            </DialogTitle>
            <DialogDescription className="py-2 text-center">
              Your form is submitted successfully. Please wait for 48 hours
              while we review your inquiry.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button
              type="button"
              className="w-full"
              onClick={() => {
                setShowSuccessModal(false);
                // Root navigation (no admin prefix), same flow as SuperAdminLayout.
                rootNavigate("/");
              }}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
